import React from 'react';
import { useSelector } from 'react-redux';
import StatsLoader from '@copilot/common/components/statistics/loaders/base';
import StatisticsSummary from '@copilot/common/components/statistics/summary';
import { StatsManager, OrganizationManager } from '@copilot/data';
import { useStatOptions } from '@copilot/common/hooks/statistics';
import { OrganizationSelectors } from '@copilot/common/store/selectors/organization';
import { useFetch } from '@copilot/common/hooks/common';
import { OrganizationActions } from '@copilot/common/store/actions/organization';
import CampaignCollapseList from './campaignCollapse';
import BaseStatisticsSummary from '@copilot/common/components/statistics/summary/base';
import AreaGraph from '@copilot/common/components/charts/simple/area';
import withPercentageCharts from '@copilot/common/components/statistics/summary/withPercentageCharts';
import {
    CampaignPercentageStatsWithReplyOverNurture,
    OrganizationPercentageStats,
    PercentageTypeExtract,
} from '@copilot/common/components/statistics/type';
import { StatsUtilityFunctions } from '@copilot/common/utils/common/stats';
import { LinkedInCalculatedStatsType } from '@copilot/common/components/componentModels/stat';
import { LinkedInStatLabelMap } from '@copilot/common/constant/enum';
import ContentWithSiderLayout from '@copilot/common/pages/layouts/contentWithSider';
import { CampaignSelectors } from '@copilot/common/store/selectors/campaign';

const loader = async (id: string, start?: Date, end?: Date) => {
    const data = await StatsManager.getStatsForOrganization(id, start, end);
    return { [id]: data };
};

/**
 * Consolidate percentage stats across selected campaigns for organization dashboard
 * @param campaignRatios percentage stats values by campaign
 * @param campaignFilters selected state of campaigns
 */
const getPercentageStats: PercentageTypeExtract = (
    campaignRatios: TypedObject<CampaignPercentageStatsWithReplyOverNurture>,
    campaignFilters: TypedObject<boolean>
) =>
    Object.keys(campaignRatios).reduce<OrganizationPercentageStats>(
        (acc, campaign) => {
            if (campaignFilters[campaign]) {
                Object.keys(acc).forEach((label) => {
                    acc[label as keyof typeof acc] = StatsUtilityFunctions.combinePercentageStats(
                        acc[label as keyof typeof acc],
                        campaignRatios[campaign][label as keyof OrganizationPercentageStats]
                    );
                });
            }
            return acc;
        },
        {
            [LinkedInCalculatedStatsType.ConnectionsOverInvites]: [
                { name: LinkedInStatLabelMap.LinkedInConnected, value: 0 },
                { name: LinkedInStatLabelMap.LinkedInInvite, value: 0 },
            ],
            [LinkedInCalculatedStatsType.ReplyOverConnections]: [
                { name: LinkedInStatLabelMap.UniqueReplies, value: 0 },
                { name: LinkedInStatLabelMap.LinkedInConnected, value: 0 },
            ],
            [LinkedInCalculatedStatsType.InterestedOverReply]: [
                { name: LinkedInStatLabelMap.ReplyInterested, value: 0 },
                { name: LinkedInStatLabelMap.UniqueReplies, value: 0 },
            ],
            [LinkedInCalculatedStatsType.ReplyOverInvites]: [
                { name: LinkedInStatLabelMap.UniqueReplies, value: 0 },
                { name: 'Invites Sent', value: 0 },
            ],
            [LinkedInCalculatedStatsType.NurtureReplyOverNurtureSent]: [
                { name: LinkedInStatLabelMap.NurtureUniqueReplies, value: 0 },
                { name: LinkedInStatLabelMap.NurtureSendMessage, value: 0 },
            ],
        }
    );

const OrganizationStatsModule = StatsLoader(
    StatisticsSummary(withPercentageCharts(BaseStatisticsSummary, getPercentageStats), AreaGraph),
    loader
);

interface OrganizationDashboardSummaryProps {
    organizationId: string;
}

const OrganizationDashboardSummary: FCWithChildren<OrganizationDashboardSummaryProps> = (
    props: OrganizationDashboardSummaryProps
) => {
    const { organizationId = '' } = props;
    const organization = useSelector(OrganizationSelectors.getOrganization(organizationId));
    const campaigns = useSelector((state) =>
        CampaignSelectors.getCampaignsByOrgId(state, props.organizationId)
    );
    const statOptionsFetcher = useFetch(StatsManager.getOrgStatFilters);
    const organizationUpdater = useFetch(
        OrganizationManager.updateOrganization,
        OrganizationActions.loadOrganization
    );

    const enabledStats = React.useMemo(
        () => ({ actionStats: organization?.actionStats, tagStats: organization?.tagStats }),
        [organization]
    );

    const [statOptions, updateFilterList] = useStatOptions(
        organizationId,
        enabledStats,
        statOptionsFetcher,
        organizationUpdater
    );

    return (
        <>
            <ContentWithSiderLayout>
                <ContentWithSiderLayout.Main>
                    <OrganizationStatsModule
                        id={organizationId}
                        title="Statistics Summary"
                        statsOptions={statOptions}
                        selectorUpdateCallback={(stats) => {
                            updateFilterList(stats);
                        }}
                        showLegends={false}
                    />
                </ContentWithSiderLayout.Main>
            </ContentWithSiderLayout>
            <CampaignCollapseList campaigns={campaigns} />
        </>
    );
};

export default OrganizationDashboardSummary;
